import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Label, ListBox, Select } from '@heroui/react';
import type { NameOption } from '@/pages/catalogImport/names';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type RowDrawerSelectProps = {
    /**
     * Which select: `unit`, `vat`, or `axis-<attribute type id>`; names its
     * node `catalog-import-row-drawer-<field>`.
     */
    field: string;
    label: string;
    /** The selected key; one the options lack (a unit the file names but no unit is) shows the placeholder. */
    value: string | null;
    /** Already localized, except the file's texts (`isFileText`), shown as "New: <text>". */
    options: NameOption[];
    onChange: (key: string) => void;
};

/**
 * A labelled HeroUI `Select` over a short list: the unit, the VAT treatment,
 * or one variation axis's values. A value of the file the import would create
 * reads "New: <text>".
 */
export const RowDrawerSelect: React.FC<RowDrawerSelectProps> = ({
    field,
    label,
    value,
    options,
    onChange,
}) => {
    const { t } = useTranslation('pages');
    const textOf = (option: NameOption): string =>
        option.isFileText
            ? t('catalogImport.products.drawer.newOption', {
                  text: option.label,
              })
            : option.label;

    return (
        <Select
            className="w-full"
            onChange={(key) => {
                if (key !== null) onChange(String(key));
            }}
            placeholder={t('catalogImport.products.drawer.placeholders.choose')}
            value={value}
        >
            <Label>{label}</Label>
            <Select.Trigger
                {...getNodeTestIdProp(`catalog-import-row-drawer-${field}`)}
            >
                <Select.Value />
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox>
                    {options.map((option) => (
                        <ListBox.Item
                            id={option.id}
                            key={option.id}
                            textValue={textOf(option)}
                        >
                            {textOf(option)}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
};
